import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Bookmark, Archive, LayoutGrid, List, Plus, Trash2 } from "lucide-react";
import type { Manual, ManualLinkCard } from "@arc/contracts";
import { facilityNavFor } from "@arc/tenant-config";
import type { AuthSession } from "../../auth/keycloak.js";
import { useMe } from "../../api/useMe.js";
import { useFacilityNavParams } from "./drilldown/useFacilityNavParams.js";
import { Button } from "@/components/ui/button";
import { ManualIcon } from "@/components/icons";
import { useShowError } from "../../error/ErrorProvider.js";
import { useFacilityManuals } from "./useFacilityManuals.js";
import { useFacilityLockFeed } from "./useFacilityLockFeed.js";
import { useFacilityCategories } from "./useFacilityCategories.js";
import { useManualFavorites } from "./useManualFavorites.js";
import {
  useArchiveManual,
  useUnarchiveManual,
  useDuplicateManual,
  useSubmitReviewManual,
  useConfirmManual,
  useUnmarkManual,
  useReturnManualToDraft,
  useManualAssignedUsers,
  useManualRatings,
  useManualLinks,
  useUnlinkManual,
  fetchManualBody,
  invalidateManualBody,
  type ManualMutationError,
} from "./useManualMutations.js";
import { useManualCheckGate } from "./manual-editor/useManualCheckGate.js";
import { ManualsToolbarControls } from "./ManualsToolbarControls.js";
import { ManualCard, type ManualCardProps } from "./ManualCard.js";
import { ManualListRow } from "./ManualListRow.js";
import { AddManualTile } from "./AddManualTile.js";
import { EMPTY_MANUAL_FILTERS, manualFiltersActive, matchesManualFilters, type ManualFilters } from "./manual-filters.js";
import { useMyManualAssignments } from "./useMyManualAssignments.js";
import { ManualDetailsDrawer } from "./ManualDetailsDrawer.js";
import { ManualViewer } from "./manual-viewer/ManualViewer.js";
import { CreateManualDrawer } from "./CreateManualDrawer.js";
import { MoveManualDrawer } from "./MoveManualDrawer.js";
import { TrashManualDialog } from "./TrashManualDialog.js";
import { ManualBinDialog, type ManualBinKind } from "./ManualBinDialog.js";
import { MarkManualDialog } from "./MarkManualDialog.js";
import { ManageReviewersDialog } from "./ManageReviewersDialog.js";
import { HandspeedPickerDialog } from "./HandspeedPickerDialog.js";
import { type ManualTransitionKey } from "./manual-transitions.js";
import { CountPill } from "./CountPill.js";
import { markNewManual } from "./manual-editor/seed-mark.js";

/** The manuals filter: everything active, only favourited, or a single category id. Archived manuals and the trash live in
 *  the area-info drawer (ADR-056). */
type ManualFilter = "all" | "favorites" | number;

/**
 * The manuals view — the right pane when a facility is selected (MV2/MV3). Fetches the facility's ACTIVE manuals and
 * (for the Archived chip) its ARCHIVED manuals, and renders a card grid with a toolbar: a filter chip row (`All` ·
 * `Favourites` · `Archived` · one per ATTACHED category — even 0-manual ones, so an empty category still shows), a
 * client-side name search, and a DISABLED "Add manual" (authoring is a later slice). Each card's kebab drives the
 * lifecycle mutations (Duplicate/Move/Archive/Unarchive/Delete — MV5); Favourites are client-only for now
 * ({@link useManualFavorites}). Loading → skeletons; a load error → an inline retry; nothing → an empty state.
 */
const REVIEW_ANNOUNCE = {
  submit: "manualManagement.announce.submitted",
  confirm: "manualManagement.announce.confirmed",
  unmark: "manualManagement.announce.unmarked",
  return: "manualManagement.announce.returned",
} as const;

export function FacilityManualsView({
  session,
  facilityId,
  canManage,
  canConfirm = false,
  facilityName = "",
  searchValue,
  onSearchChange,
  advancedFilters,
  kiosk = false,
}: {
  session: AuthSession | null;
  facilityId: number;
  /** Best-effort gate for the manual kebab actions (the caller's facility permission is `manuals_manage`+); the server
   *  is authoritative (state-dependent authz + the `confirm` nuance). */
  canManage: boolean;
  /** The caller has the facility `confirm` tier — reveals the publish/unpublish/clear-flag review transitions (ADR-018);
   *  a `manuals_manage`-only caller only sees Submit / Mark / withdraw. Defaults false (server re-checks). */
  canConfirm?: boolean;
  /** For the archive and trash dialogs of the classic pane, which has no area-info drawer. */
  facilityName?: string;
  /** Controlled search value + setter, when the SEARCH box is rendered OUTSIDE this component (the drill-down header's
   *  title row — mockup #15). When `onSearchChange` is provided the internal search row is NOT rendered (the header
   *  owns it); absent ⇒ this view renders its own controls (the classic pane). */
  searchValue?: string;
  onSearchChange?: (v: string) => void;
  /** Advanced filters from the "More filters" dialog (owned by the drill-down header). Applied over the loaded manuals;
   *  absent ⇒ no advanced filtering (the classic pane's placeholder button doesn't set them). */
  advancedFilters?: ManualFilters;
  /**
   * Shop-floor kiosk (ADR-036). Drops the FAVOURITES chip — the account is shared, so "my favourites" would be
   * everyone's — and the ARCHIVED chip: a kiosk exists to show the CURRENT work instruction, and in a regulated plant
   * an operator must not be one tap away from a withdrawn one. The legacy hid its kiosk chrome with `display: none`,
   * leaving every control in the DOM; here they are not rendered.
   */
  kiosk?: boolean;
}) {
  const { t } = useTranslation();
  const active = useFacilityManuals(session, facilityId, false);
  const archived = useFacilityManuals(session, facilityId, true);
  // Live padlocks: while this list is open, the facility room patches each card's `locked` as colleagues open and leave
  // editors — so a second window no longer has to be reloaded to notice the manual was taken (ADR-026).
  useFacilityLockFeed(session, facilityId);
  // ALL attached categories (not just those with manuals) — so an empty category (e.g. Anleitungen) still gets a chip.
  const attachedCategories = useFacilityCategories(session, facilityId);
  const { favorites, toggle } = useManualFavorites(session);
  // The caller's local user id (/me.id) — the View/Edit gate's assignment check (ADR-002 Phase A). Shares the one `me`
  // cache entry with the header/account panel; `null` until it loads (Edit stays disabled, never a false-enable).
  const me = useMe(session);
  const myId = me.data?.id ?? null;
  // The runtime viewer (ADR-002 Phase 5) — opened over everything from the drawer's View button; the drawer stays open
  // behind it, so closing the viewer returns to the drawer.
  const [viewingManual, setViewingManual] = useState<Manual | null>(null);
  const [filter, setFilter] = useState<ManualFilter>("all");
  // Search is controlled when the header renders the box (drill-down #15); else internal (classic pane).
  const externalToolbar = onSearchChange !== undefined;
  const [searchInternal, setSearchInternal] = useState("");
  const search = searchValue ?? searchInternal;
  const setSearch = onSearchChange ?? setSearchInternal;

  // Lifecycle mutations (MV4 API). Archive/unarchive/duplicate act immediately (invalidate → the grid refetches); Move
  // opens a drawer; Delete opens a type-to-confirm dialog. Errors route to the shared error dialog.
  const showError = useShowError();
  const archiveM = useArchiveManual(session);
  const unarchiveM = useUnarchiveManual(session);
  const duplicateM = useDuplicateManual(session);
  // Review transitions (ADR-018). `mark` opens a comment dialog; the other four fire directly (server CAS is the guard).
  const qc = useQueryClient();
  // The SAME Check Manual gate the editor uses (ADR-018 addendum §9). This door exists only for classic-nav realms,
  // which is exactly why it needs the gate: they have no editor to reach the other one.
  const { gate, dialog: checkDialog } = useManualCheckGate();
  /** True from the moment a publish is asked for until its gate concludes — see `onTransition`. */
  const [deciding, setDeciding] = useState(false);
  const submitM = useSubmitReviewManual(session);
  const confirmM = useConfirmManual(session);
  const unmarkM = useUnmarkManual(session);
  const returnM = useReturnManualToDraft(session);
  // Linked manuals (ADR-020): create a link (opens the placement drawer in link mode — create-and-place atomic) + unlink
  // a child from the drawer.
  const unlinkM = useUnlinkManual(session);
  // `linkMode` flags the placement drawer as CREATE-A-LINK (pick a target facility/category → create the copy there),
  // vs a plain manual MOVE.
  const [movingManual, setMovingManual] = useState<{ id: number; name: string; linkMode?: boolean } | null>(null);
  const [deletingManual, setDeletingManual] = useState<Manual | null>(null);
  const [bin, setBin] = useState<ManualBinKind | null>(null);
  const [markingManual, setMarkingManual] = useState<Manual | null>(null);
  const [managingReviewers, setManagingReviewers] = useState<{ id: number; name: string } | null>(null);
  const [handspeedPicker, setHandspeedPicker] = useState<{ id: number; name: string } | null>(null);
  const [detailsManual, setDetailsManual] = useState<Manual | null>(null);
  /** Whether the OPEN drawer's manual is archived — resolved when it is opened (from its list, or the arc_details
   *  lookup), never derived from the live `filter`: a later filter write (facility reset, category cleanup) must not
   *  relabel an archived manual's drawer as active (panel). */
  const [detailsArchived, setDetailsArchived] = useState(false);
  const [creating, setCreating] = useState(false);
  // Best-effort, the server is authoritative. Not rendered at all without permission or on a kiosk (ADR-036).
  const canAdd = canManage && !kiosk;
  const [view, setView] = useState<"grid" | "list">("grid");
  const [announce, setAnnounce] = useState("");
  // Inline error + per-child pending for the drawer's Linked-manuals actions (create-link / unlink).
  const [linkError, setLinkError] = useState<string | null>(null);
  const [unlinkingId, setUnlinkingId] = useState<number | null>(null);
  // The open manual's assigned reviewers (ADR-019) — lazily fetched only while the details drawer is open; the drawer
  // renders the list read-only (interactive assign is deferred).
  const assigned = useManualAssignedUsers(session, detailsManual?.id ?? null);
  // The open manual's rating summary (ADR-021) — same lazy-when-open pattern; the drawer header shows the average.
  const ratings = useManualRatings(session, detailsManual?.id ?? null);
  // The open manual's linked manuals (ADR-020) — parent + children; the drawer renders them + a "go" arrow.
  const links = useManualLinks(session, detailsManual?.id ?? null);
  // Linked-manual navigation (ADR-020): the drawer's link-card arrow jumps to the target manual's facility + opens its
  // drawer, via the URL nav (`arc_facility`+`arc_manual`). Only wired in the DRILL-DOWN realms (classic has no
  // arc_facility view to react); `navigable` gates the arrow off elsewhere.
  const { viewManual, editManual, openManualDetails, manualsId, detailsManualId, dropStale } = useFacilityNavParams();
  const navigable = session !== null && facilityNavFor(session.realm) === "drilldown";

  const onUnarchive = (m: Manual) => unarchiveM.mutate({ id: m.id, expectedFrom: m.state }, { onSuccess: () => setAnnounce(t("manualManagement.announce.unarchived", { name: m.name })), onError: onLifecycleError(m) });
  const onDuplicate = (m: Manual) => duplicateM.mutate(m.id, { onSuccess: () => setAnnounce(t("manualManagement.announce.duplicated", { name: m.name })), onError: showError });

  // Create a linked copy of the open (published) manual (ADR-020): open the placement drawer in LINK mode — the caller
  // picks the target facility/category and the copy is created-and-placed there atomically (create IS the placement).
  const onCreateLink = (m: Manual) => {
    setLinkError(null);
    setDetailsManual(null);
    setMovingManual({ id: m.id, name: m.name, linkMode: true });
  };
  // Remove a linked child (ADR-020) — the × on a child card. Inline error + per-child spinner (no confirm dialog: a link
  // is a lightweight copy, and the drawer refreshes it out on success).
  const onUnlink = (card: ManualLinkCard) => {
    setLinkError(null);
    setUnlinkingId(card.id);
    unlinkM.mutate(card.id, {
      onSuccess: () => {
        setUnlinkingId(null);
        setAnnounce(t("manualManagement.announce.unlinked", { name: card.name }));
      },
      onError: (e) => {
        setUnlinkingId(null);
        setLinkError(linkErrorCopy(e, t));
      },
    });
  };

  // Keep the open details drawer in sync when its manual transitions (workflow continuity — the drawer stays open on the
  // new state, showing the now-available actions). The grid itself refetches via the hook's invalidate.
  const refreshDrawer = (updated: Manual) =>
    setDetailsManual((cur) => (cur && cur.id === updated.id ? updated : cur));
  // A CAS 409 (the manual moved/changed under us) means our `expectedFrom` — and any open snapshot of this manual — is
  // stale. Refetch the lists for the truth AND drop the open drawer for this manual (its state badge + transition buttons
  // carry the stale `expectedFrom`; keeping it open would let a re-click re-send the same losing CAS in a 409 loop — panel
  // finding). Reopening from the refreshed card carries the fresh state. `id === null` = don't know which (mark dialog).
  const reconcileStale = (id: number | null) => {
    void active.refetch();
    void archived.refetch();
    setDetailsManual((cur) => (cur && (id === null || cur.id === id) ? null : cur));
  };

  // Archive / unarchive carry the card's believed state like the 5 transitions; a 409 STALE is the same expected race →
  // the same quiet reconcile (refetch + drop the drawer + announce), never the error dialog — else the stale card would
  // re-send the same losing belief in a 409 loop (panel).
  const onLifecycleError = (m: Manual) => (err: unknown) => {
    if ((err as ManualMutationError).code === "MANUAL_STATE_STALE") {
      reconcileStale(m.id);
      setAnnounce(`${m.name} changed since you opened it — the list has been refreshed.`);
    } else {
      showError(err);
    }
  };
  const onArchive = (m: Manual) => archiveM.mutate({ id: m.id, expectedFrom: m.state }, { onSuccess: () => setAnnounce(t("manualManagement.announce.archived", { name: m.name })), onError: onLifecycleError(m) });

  // A review transition (ADR-018). `mark` needs a comment → open the dialog; the other four are atomic CAS mutations that
  // carry the client's believed state as `expectedFrom`. A 409 STALE means it moved under us → reconcile (no error dialog
  // for that expected race — a quiet refresh + announce). Other failures route to the shared error dialog.
  // WHO owns the review workflow (ADR-018 addendum §3). Where the editor is reachable it does, and the list + drawer
  // become status-DISPLAY only — the Check Manual gate is only meaningful with one door to publishing.
  //
  // Classic-nav realms have NO editor at all (`onEdit` is hardwired undefined when `!navigable`), so sweeping the
  // transitions out of this view unconditionally would leave novonordisk / lumpi / abzbraunau with no way to submit,
  // confirm, unmark or return a manual — three of five tenants, silently (diff panel, High).
  //
  // So this door stays, and since the addendum §9 it carries the Check Manual gate too. It used to be justified as
  // "no editor, so no gate to protect" — but the gate is a property of PUBLISHING, not of the editor, and the legacy
  // (whose manual view every realm reaches) checks these three realms as well. Leaving it out meant the majority of
  // tenants published unchecked.
  const ownsReviewWorkflow = !navigable;

  /**
   * A transition asked for from the LIST (classic-nav realms only — see `ownsReviewWorkflow`).
   *
   * The publish direction goes through Check Manual first, exactly as the editor's door does. The list holds manual
   * SUMMARIES, not bodies, so the check's input is fetched here at the moment it is asked for. A failed fetch does
   * NOT block: the check is advisory and the server enforces authz and the state CAS regardless, so refusing to
   * publish because a summary could not be computed would fail in the wrong direction — the transition proceeds
   * ungated, which is the behaviour these realms had before the gate existed.
   */
  const onTransition = (m: Manual, key: ManualTransitionKey) => {
    if (key === "mark") {
      setMarkingManual(m);
      return;
    }
    if (key === "submit" || key === "confirm") {
      // FIRST-WINS while a publish is being decided (diff panel, Medium). The gate holds ONE pending slot, and this
      // view can ask it about any manual in the list — so a second Submit landing before the first body arrives would
      // overwrite the first's `proceed` and the author would confirm a manual they were not looking at, with the one
      // they chose silently dropped. Only the FETCH window is exposed (once the dialog is up it is modal and the
      // kebabs are unreachable), so this ignores a few hundred milliseconds of double-clicking and nothing more.
      if (deciding) return;
      setDeciding(true);
      void fetchManualBody(qc, session, m.id)
        .catch(() => null)
        .then((body) => gate(body, key, () => runTransition(m, key), () => setDeciding(false)));
      return;
    }
    runTransition(m, key);
  };

  const runTransition = (m: Manual, key: Exclude<ManualTransitionKey, "mark">) => {
    const hook = { submit: submitM, confirm: confirmM, unmark: unmarkM, return: returnM }[key];
    // A literal key map, not a built string: typed keys reject a template, and that rejection is worth keeping —
    // a mistyped state would otherwise announce the raw key path to a screen reader.
    const verb = t(REVIEW_ANNOUNCE[key]);
    hook.mutate(
      { id: m.id, expectedFrom: m.state },
      {
        onSuccess: (updated) => {
          setAnnounce(`${verb}: ${updated.name}.`);
          refreshDrawer(updated);
          // The BODY cache must be dropped too (diff panel, Medium). The transition hooks invalidate the manuals
          // LISTS but deliberately not the body key, and since §9 this view WRITES that key — `fetchManualBody`
          // leaves a pre-transition body behind with `dataUpdatedAt = now`. Opening the reader inside the 30 s
          // staleTime would then read the OLD `manual.state`: a wrong state badge, and `canPrint`/`canReport`
          // (ManualViewer:63,67) both false on a manual that was just confirmed. The editor's door has always done
          // this; becoming a body-cache writer is what put this door in the same position.
          invalidateManualBody(qc, session, m.id);
        },
        onError: (err) => {
          if ((err as ManualMutationError).code === "MANUAL_STATE_STALE") {
            reconcileStale(m.id);
            setAnnounce(`${m.name} changed since you opened it — the list has been refreshed.`);
          } else {
            showError(err);
          }
        },
      },
    );
  };

  // Reset the toolbar when the selected facility changes (its categories differ) — but NOT the favourites (they're keyed
  // by manual id, so they stay correct across facilities, mirroring how F1 will persist them).
  useEffect(() => {
    setFilter("all");
    setSearch("");
    // The drawer is facility-local state: browser Back from a cross-facility link must not leave the OTHER facility's
    // drawer over this list (panel). Declared before the arc_details effect, so a legitimate cross-facility open still
    // re-sets it in the same commit.
    setDetailsManual(null);
  }, [facilityId]);

  // `?arc_details=<id>` — a linked-manual card's navigation (PARITY-GAPS §2): open THAT manual's details drawer over this
  // list, also when it is archived (the reader can't show an archived manual; the drawer can). ONE-SHOT: consumed with `dropStale` once resolved — also when the id is not in either list (stale / not
  // visible here), so a bad deep-link never pins the param. Waits for BOTH lists so an archived target isn't judged
  // "missing" while only the active list has arrived.
  useEffect(() => {
    if (detailsManualId === null) return;
    // Only the view of the facility the URL names may resolve it: a link to ANOTHER facility updates the URL first and
    // this (still-mounted, old-facility) instance must not judge the id "missing" against its own lists and consume it.
    if (manualsId !== facilityId) return;
    // "Not found" is a verdict only BOTH lists can give: a failed fetch (error state) keeps the param, so the user's
    // "Try again" still opens the drawer (panel).
    if (!active.isSuccess || !archived.isSuccess || active.isPlaceholderData || archived.isPlaceholderData) return;
    const inActive = active.data.find((m) => m.id === detailsManualId) ?? null;
    const inArchived = inActive ? null : (archived.data.find((m) => m.id === detailsManualId) ?? null);
    if (inActive) {
      setDetailsArchived(false);
      setDetailsManual(inActive);
    } else if (inArchived) {
      setDetailsArchived(true);
      setDetailsManual(inArchived);
    }
    dropStale("arc_details");
  }, [detailsManualId, manualsId, facilityId, active.isSuccess, archived.isSuccess, active.isPlaceholderData, archived.isPlaceholderData, active.data, archived.data, dropStale]);

  // If the category we're filtered by is DETACHED (e.g. via the Manage drawer) it disappears from the attached set on
  // refetch — but the `[facilityId]` reset above didn't fire, so `filter` would strand a stale category id that matches
  // nothing (a chip-less "No manuals match your filters" dead-end; diff panel #1). Reset to "all" when the active
  // numeric filter is no longer an attached category. Only acts once the (authoritative) attached set has loaded.
  useEffect(() => {
    const attached = attachedCategories.data;
    if (!attached) return;
    // Functional: re-validates the CURRENT value, so it can never clobber a filter another effect wrote in the same
    // commit (the arc_details "archived" switch — panel).
    setFilter((f) => (typeof f === "number" && !attached.some((c) => c.id === f) ? "all" : f));
  }, [attachedCategories.data, filter]);

  const activeList = active.data ?? [];
  const favCount = useMemo(() => activeList.filter((m) => favorites.has(m.id)).length, [activeList, favorites]);

  // Per-category ACTIVE manual counts (drives the chip counts; matches "All" = active count).
  const countByCategory = useMemo(() => {
    const m = new Map<number, number>();
    for (const x of activeList) m.set(x.categoryId, (m.get(x.categoryId) ?? 0) + 1);
    return m;
  }, [activeList]);

  // Chips come from the facility's ATTACHED categories (server-sorted by name), so a category with 0 manuals still shows.
  // If that query is loading OR failed, we DON'T lose the chips: fall back to categories derived from the loaded manuals
  // (non-empty ones only) so filtering still works — the empty-category chips just wait for the categories query (panel).
  const categories = useMemo(() => {
    if (attachedCategories.data) {
      return attachedCategories.data.map((c) => ({ id: c.id, name: c.name, count: countByCategory.get(c.id) ?? 0 }));
    }
    const byId = new Map<number, { id: number; name: string; count: number }>();
    for (const m of activeList) {
      const e = byId.get(m.categoryId);
      if (e) e.count++;
      else byId.set(m.categoryId, { id: m.categoryId, name: m.categoryName, count: 1 });
    }
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [attachedCategories.data, activeList, countByCategory]);

  // Advanced filters (the "More filters" dialog) stack on top of the chip + search filters.
  const adv = advancedFilters ?? EMPTY_MANUAL_FILTERS;
  // The ids-only assignments read — fetched once per session, only consulted when the switch is on. `null` while it
  // is not known (loading / failed): the predicate then passes everything and the status line below explains, instead
  // of the list silently collapsing to "no manuals match" (diff-panel).
  const assignments = useMyManualAssignments(session);
  const assignedIds = useMemo<ReadonlySet<number> | null>(() => (assignments.data ? new Set(assignments.data) : null), [assignments.data]);
  const filtered = useMemo(() => {
    let out = activeList;
    if (filter === "favorites") out = out.filter((m) => favorites.has(m.id));
    else if (typeof filter === "number") out = out.filter((m) => m.categoryId === filter);
    const q = search.trim().toLowerCase();
    if (q) out = out.filter((m) => m.name.toLowerCase().includes(q));
    if (manualFiltersActive(adv)) out = out.filter((m) => matchesManualFilters(m, adv, { myUsername: session?.username ?? null, assignedIds }));
    return out;
  }, [activeList, filter, favorites, search, adv, session?.username, assignedIds]);

  const current = active;
  const baseList = activeList;

  // The identical wiring a manual needs in EITHER layout (grid card ⇄ list row) — so the two share one source of truth.
  const itemProps = (m: Manual): ManualCardProps => ({
    manual: m,
    archived: false,
    canManage,
    canConfirm,
    isFavorite: favorites.has(m.id),
    onToggleFavorite: toggle,
    onOpenDetails: (m) => {
      setDetailsArchived(false);
      setDetailsManual(m);
    },
    onArchive,
    onUnarchive,
    onDuplicate,
    onMove: setMovingManual,
    onDelete: setDeletingManual,
    onTransition: ownsReviewWorkflow ? onTransition : undefined,
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Toolbar. In the drill-down (#15) the search + Add live in the HEADER (externalToolbar) and only the filter row
          renders here; the classic pane renders its own controls above the filter row. Full-width divider bar; content
          centres at the count-based width so it lines up with the grid. */}
      <div className="border-border/60 bg-background border-b py-3">
        {/* Shared content max-width (mirrors the header + grid): wide enough that the tabs + category chips never wrap
            on a few-manual facility. */}
        <div className="page-column flex flex-col gap-3">
        {!externalToolbar ? (
          <ManualsToolbarControls
            search={search}
            onSearchChange={setSearch}
          />
        ) : null}
        {/* The classic pane has no area-info drawer for most callers, so the two lists open from here (ADR-056). */}
        {!externalToolbar ? (
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button variant="outline" size="sm" onPress={() => setBin("archived")}>
              <Archive aria-hidden="true" />
              {t("facilityManagement.overview.showArchivedPlain")}
            </Button>
            {canManage ? (
              <Button variant="outline" size="sm" onPress={() => setBin("trash")}>
                <Trash2 aria-hidden="true" />
                {t("manualSelection.facilitySidebar.openTrash")}
              </Button>
            ) : null}
          </div>
        ) : null}

        {/* Left-aligned under the title, as in the facility selection (proposal 2026-11-04; it was centred). On a phone ONE
            line that scrolls sideways (the chips took three rows there before the first manual). */}
        <div className="flex flex-wrap items-center justify-start gap-2 [scrollbar-width:none] max-sm:-mx-3 max-sm:flex-nowrap max-sm:justify-start max-sm:overflow-x-auto max-sm:px-3 max-sm:[&_*]:shrink-0">
          <div className="flex flex-wrap items-center justify-start gap-2 max-sm:flex-nowrap">
            {/* Status tabs: All / Favourites / Archived. */}
            <div className="flex flex-wrap items-center justify-start gap-1 max-sm:flex-nowrap" role="group" aria-label={t("manualManagement.list.filter")}>
              <Button variant={filter === "all" ? "secondary" : "ghost"} size="sm" aria-pressed={filter === "all"} onPress={() => setFilter("all")}>
                {t("common.all")}
                <CountPill n={activeList.length} />
              </Button>
              {kiosk ? null : (
                <>
              <Button variant={filter === "favorites" ? "secondary" : "ghost"} size="sm" aria-pressed={filter === "favorites"} onPress={() => setFilter("favorites")}>
                <Bookmark className="size-3.5" aria-hidden="true" />
                {t("manualSelection.categoryFavourites")}
                <CountPill n={favCount} />
              </Button>
                </>
              )}
            </div>

            {/* Category chips — the facility's attached categories, separated from the status tabs by a thin divider. */}
            {categories.length > 0 ? (
              <>
                <span aria-hidden="true" className="bg-border mx-1 hidden h-5 w-px self-center sm:block" />
                <div className="flex flex-wrap items-center justify-start gap-1 max-sm:flex-nowrap" role="group" aria-label={t("manualManagement.list.filterByCategory")}>
                  {categories.map((cat) => (
                    <Button
                      key={cat.id}
                      variant={filter === cat.id ? "secondary" : "ghost"}
                      size="sm"
                      aria-pressed={filter === cat.id}
                      onPress={() => setFilter(cat.id)}
                    >
                      <span className="max-w-[10rem] truncate">{cat.name}</span>
                      <CountPill n={cat.count} />
                    </Button>
                  ))}
                </div>
              </>
            ) : null}
          </div>

          {/* Grid / list view toggle (mockup #14), with the add at the row's end: in a long list the tile is far below. */}
          <div className="border-border ml-auto flex shrink-0 items-center gap-0.5 rounded-md border p-0.5 max-sm:ml-0" role="group" aria-label={t("manualManagement.list.view")}>
            <Button variant={view === "grid" ? "secondary" : "ghost"} size="icon-sm" aria-label={t("manualManagement.list.gridView")} aria-pressed={view === "grid"} onPress={() => setView("grid")}>
              <LayoutGrid aria-hidden="true" className="size-4" />
            </Button>
            <Button variant={view === "list" ? "secondary" : "ghost"} size="icon-sm" aria-label={t("manualManagement.list.listView")} aria-pressed={view === "list"} onPress={() => setView("list")}>
              <List aria-hidden="true" className="size-4" />
            </Button>
          </div>
          {canAdd ? (
            // Orange, icon + text: it adds content (owner rule 2026-10-21/22).
            <Button variant="accent" size="sm" onPress={() => setCreating(true)} className="shrink-0 gap-1.5">
              <Plus aria-hidden="true" />
              {t("manualManagement.toolbar.add")}
            </Button>
          ) : null}
        </div>
        </div>
      </div>

      {/* Content: skeletons while loading, an inline retry on error, an empty state, else the card grid. Full-width
          scroll area; the grid centres at the same count-based width as the toolbar. */}
      {/* The grey band the cards sit on, as in the facility selection (design pass 2026-11-04). */}
      <div className="bg-muted min-h-0 flex-1 overflow-y-auto py-5">
        <div className="page-column">
        {/* The "Assigned to me" switch is on but the assignments read is not there yet (loading, or it failed): the
            predicate is SKIPPED rather than rejecting everything, and this line says why the list is unfiltered —
            "no manuals match" with no error and no retry was the silent alternative (diff-panel). */}
        {adv.assignedToMe && assignedIds == null ? (
          <p role="status" className="text-muted-foreground flex flex-wrap items-center gap-2 text-xs">
            {assignments.isError ? (
              <>
                {t("manualManagement.list.assignmentsFailed")}
                <Button variant="ghost" size="xs" onPress={() => void assignments.refetch()} isDisabled={assignments.isFetching}>
                  {t("common.retry")}
                </Button>
              </>
            ) : (
              t("manualManagement.list.loadingAssignments")
            )}
          </p>
        ) : null}
        {current.isLoading ? (
          <ManualGrid>
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="border-border bg-muted/40 h-32 animate-pulse rounded-lg border" aria-hidden="true" />
            ))}
          </ManualGrid>
        ) : current.isError ? (
          <div className="text-muted-foreground flex flex-col items-center justify-center gap-3 py-12 text-center">
            <p className="text-sm">{t("manualManagement.list.loadFailed")}</p>
            <Button
              variant="outline"
              size="sm"
              onPress={() => {
                void current.refetch();
                void attachedCategories.refetch(); // retry the chips too, in case that request also failed
              }}
              isDisabled={current.isFetching}
            >
              {t("common.tryAgain")}
            </Button>
          </div>
        ) : (
          <>
            {/* The add tile stays with no manuals or no matches, or a new facility couldn't get its first one. */}
            {!canAdd && filtered.length === 0 ? null : view === "list" ? (
              <ManualList>
                {filtered.map((m) => (
                  <ManualListRow key={m.id} {...itemProps(m)} />
                ))}
                {canAdd ? <AddManualTile layout="list" onPress={() => setCreating(true)} /> : null}
              </ManualList>
            ) : (
              <ManualGrid>
                {filtered.map((m) => (
                  <ManualCard key={m.id} {...itemProps(m)} />
                ))}
                {canAdd ? <AddManualTile layout="grid" onPress={() => setCreating(true)} /> : null}
              </ManualGrid>
            )}
            {baseList.length === 0 ? (
              <EmptyState icon line={t("manualManagement.list.noManuals")} hint={t("manualManagement.list.noManualsHint")} />
            ) : filtered.length === 0 ? (
              <EmptyState line={t("manualManagement.list.noMatches")} hint={t("manualManagement.list.noMatchesHint")} />
            ) : null}
          </>
        )}
        </div>
      </div>

      {creating ? (
        <CreateManualDrawer
          facilityId={facilityId}
          session={session}
          onClose={() => setCreating(false)}
          onCreated={(created) => {
            setAnnounce(t("manualManagement.announce.created", { name: created.name }));
            // Straight into the editor, which gives it a first section and step (owner 2026-11-04). Classic-nav realms
            // have no editor, so they stay on the list.
            if (navigable) {
              markNewManual(created.id);
              editManual(created.id);
            }
          }}
        />
      ) : null}
      {movingManual ? (
        <MoveManualDrawer
          manual={movingManual}
          session={session}
          onClose={() => setMovingManual(null)}
          linkMode={movingManual.linkMode}
          onMoved={(name, fac, cat) =>
            setAnnounce(
              movingManual.linkMode
                ? t("manualManagement.announce.linked", { name, target: `${fac}${cat ? ` · ${cat}` : ""}` })
                : t("manualManagement.announce.moved", { name, target: `${fac}${cat ? ` · ${cat}` : ""}` }),
            )
          }
        />
      ) : null}
      {bin ? (
        <ManualBinDialog kind={bin} facilityId={facilityId} facilityName={facilityName} canManage={canManage} session={session} onClose={() => setBin(null)} />
      ) : null}
      {deletingManual ? (
        <TrashManualDialog
          manual={deletingManual}
          session={session}
          onClose={() => setDeletingManual(null)}
          onTrashed={(name) => setAnnounce(t("manualManagement.announce.trashed", { name }))}
        />
      ) : null}
      {/* Manual details drawer (opened from a card title). A kebab action inside closes it first (setDetailsManual(null))
          then runs the action — the acted-on manual leaves/relocates, so the snapshot drawer shouldn't linger. */}
      {detailsManual ? (
        <ManualDetailsDrawer
          manual={detailsManual}
          archived={detailsArchived}
          canManage={canManage}
          canConfirm={canConfirm}
          myId={myId}
          // Open the full-screen reader. In the drill-down realms it's a URL route (`?arc_manual=<id>`, a clean shareable
          // link rendered by the drill-down — no manuals-view flash); classic (no URL nav) opens it via local state.
          // ALSO close this details drawer: in classic it would otherwise stay mounted BEHIND the reader and, after a
          // report/mark inside the reader, reappear on close holding the STALE pre-report state + obsolete transitions
          // (panel). The card re-open shows fresh state. (In drill-down `viewManual` unmounts the manuals view anyway.)
          onView={(m) => {
            setDetailsManual(null);
            if (navigable) viewManual(m.id);
            else setViewingManual(m);
          }}
          // Open the full-screen AUTHORING editor (ADR-002 Phase 10) — a URL route (`?arc_edit=<id>`) in the drill-down
          // realms; the drawer's Edit button is gated on `canEdit` (confirm-tier OR manuals_manage+assigned). Classic (no
          // URL nav) keeps Edit disabled for now — the drill-down is the authoring surface.
          onEdit={navigable ? (m) => { setDetailsManual(null); editManual(m.id); } : undefined}
          shareable={navigable}
          isFavorite={favorites.has(detailsManual.id)}
          onToggleFavorite={toggle}
          onDuplicate={(m) => {
            setDetailsManual(null);
            onDuplicate(m);
          }}
          onMove={(m) => {
            setDetailsManual(null);
            setMovingManual(m);
          }}
          onArchive={(m) => {
            setDetailsManual(null);
            onArchive(m);
          }}
          onUnarchive={(m) => {
            setDetailsManual(null);
            onUnarchive(m);
          }}
          onDelete={(m) => {
            setDetailsManual(null);
            setDeletingManual(m);
          }}
          onTransition={ownsReviewWorkflow ? onTransition : undefined}
          assignedUsers={assigned.data}
          assignedUsersLoading={assigned.isPending}
          assignedUsersError={assigned.isError}
          // Opens the Manage dialog OVER the drawer (doesn't close it — like Mark) so the reviewer list refreshes in place.
          onManageReviewers={(m) => setManagingReviewers({ id: m.id, name: m.name })}
          session={session}
          onChangeHandspeed={(m) => setHandspeedPicker({ id: m.id, name: m.name })}
          // Patch the open snapshot: `detailsManual` is view state (the URL sync is one-shot), so without this the
          // header's "Handspeed" badge would keep the value the drawer opened with until it is reopened.
          onHandspeedChange={(id, enabled) =>
            setDetailsManual((cur) => (cur && cur.id === id ? { ...cur, handspeedCheck: enabled } : cur))
          }
          ratingAverage={ratings.data?.average ?? null}
          ratingCount={ratings.data?.count ?? 0}
          ratingsLoading={ratings.isPending}
          ratingsError={ratings.isError}
          links={links.data}
          linksLoading={links.isPending}
          linksError={links.isError}
          // A linked-manual card → the TARGET's facility manuals view with ITS drawer open (not the reader: an archived
          // target has no reader, and the drawer is where the link chain is visible). Same facility ⇒ the view stays
          // mounted and the `arc_details` effect swaps the drawer.
          onNavigateManual={
            navigable
              ? (card) => {
                  setDetailsManual(null);
                  // Same facility: the view stays mounted and the param is consumed at once — REPLACE, or history holds a
                  // twin entry and the first Back is a dead press (panel).
                  openManualDetails(card.facilityId, card.id, card.facilityId === facilityId);
                }
              : undefined
          }
          onCreateLink={onCreateLink}
          onUnlink={onUnlink}
          unlinkingId={unlinkingId}
          linkActionError={linkError}
          onClose={() => {
            setLinkError(null);
            setDetailsManual(null);
          }}
        />
      ) : null}
      {/* Classic realms only render the reader here (local state); drill-down realms route it through `?arc_manual` at the
          drill-down level (no URL nav in classic). */}
      {viewingManual ? (
        // `key` by the manual id → a fresh reader instance per manual (no started/step/finished state leak; panel High).
        <ManualViewer key={viewingManual.id} session={session} manualId={viewingManual.id} onClose={() => setViewingManual(null)} />
      ) : null}
      {/* Check Manual (ADR-018 addendum §9) — the pre-publish summary before submit/confirm, the same gate and the
          same dialog the editor's status control opens. Only classic-nav realms ever reach THIS door. */}
      {checkDialog}
      {/* Mark-as-faulty comment capture (ADR-018) — opened by the `mark` transition from a card kebab OR the open drawer.
          Unlike the lifecycle dialogs it does NOT close the drawer first; on success it refreshes it in place. */}
      {markingManual ? (
        <MarkManualDialog
          manual={{ id: markingManual.id, name: markingManual.name, state: markingManual.state }}
          session={session}
          onClose={() => setMarkingManual(null)}
          onMarked={(updated) => {
            setAnnounce(t("manualManagement.announce.marked", { name: updated.name }));
            refreshDrawer(updated);
          }}
          // A CAS 409 inside the dialog: refresh the lists + drop this manual's stale drawer, so closing then reopening
          // from the refreshed card carries a fresh `expectedFrom` (else the reopened dialog re-sends the loser — panel).
          onStale={() => reconcileStale(markingManual.id)}
        />
      ) : null}
      {/* Manage reviewers (ADR-022) — opened from the drawer's "Assigned users" Manage button; layered OVER the drawer.
          On close, the drawer's assigned-users query has been invalidated by the assign/unassign mutations → refreshes. */}
      {managingReviewers ? (
        <ManageReviewersDialog manual={managingReviewers} session={session} onClose={() => setManagingReviewers(null)} />
      ) : null}
      {/* Hand-speed devices (ADR-039) — opened from the drawer's Handspeed section, layered OVER the drawer. The save
          seeds the shared query, so the section behind it is already correct when this closes. */}
      {handspeedPicker ? (
        <HandspeedPickerDialog manual={handspeedPicker} session={session} onClose={() => setHandspeedPicker(null)} />
      ) : null}
      {/* SR-only live region — announces the outcome of a kebab action (the grid refetch is the visual cue). */}
      <p aria-live="polite" className="sr-only">
        {announce}
      </p>
    </div>
  );
}

/** Card grid — fixed-width cards CENTRED within the content band (`auto-fit` collapses the empty tracks,
 *  `justify-center` centres the rest), so the manuals sit centralised on the same axis as the centred chip row above
 *  them (not floating away from left-aligned chips — the earlier miss). Fixed 15rem tracks (not `1fr`) keep the cards a
 *  normal size for a 2-manual facility; a full facility fills the band and wraps. The header stays full-width, apart. */
function ManualGrid({ children }: { children: React.ReactNode }) {
  // A phone gets the full width: a 15rem tile in the middle of it wasted a third of the screen.
  return <div className="grid grid-cols-[repeat(auto-fill,minmax(15rem,15rem))] justify-start gap-4 max-sm:grid-cols-1 max-sm:gap-3">{children}</div>;
}

/** The list-view container — bordered rows (each {@link ManualListRow} carries its own divider; the last one is dropped). */
function ManualList({ children }: { children: React.ReactNode }) {
  return <div className="border-border overflow-hidden rounded-lg border [&>*:last-child]:border-b-0">{children}</div>;
}

function EmptyState({ icon, line, hint }: { icon?: boolean; line: string; hint?: string }) {
  return (
    <div className="text-muted-foreground flex flex-col items-center justify-center gap-2 py-12 text-center">
      {icon ? <ManualIcon aria-hidden="true" className="size-8 opacity-40" /> : null}
      <p className="text-sm">{line}</p>
      {hint ? <p className="text-xs opacity-80">{hint}</p> : null}
    </div>
  );
}

/** Inline copy for a failed create-link / unlink (ADR-020), by the contract's machine `code`. */
function linkErrorCopy(err: unknown, t: TFunction): string {
  const code = (err as ManualMutationError | undefined)?.code;
  if (code === "MANUAL_LINK_SOURCE_NOT_PUBLISHED") return t("manualManagement.link.confirmFirst");
  if (code === "MANUAL_NOT_LINKED") return t("manualManagement.link.notALink");
  if (code === "MANUAL_FORBIDDEN") return t("manualManagement.link.forbidden");
  if (code === "MANUAL_NOT_FOUND") return t("manualManagement.link.gone");
  return t("manualManagement.link.failed");
}
